import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Spinner } from '@/components/ui/spinner';

type ErrorDetails = {
  name?: string | null;
  code?: string | null;
  message?: string | null;
  timestamp?: string | null;
};

type ErrorAlertProps = {
  error: string;
  errorDetails?: ErrorDetails | null;
  onRetry?: () => void;
  isRetrying?: boolean;
  className?: string;
};

export const ErrorAlert = ({
  error,
  errorDetails,
  onRetry,
  isRetrying = false,
  className,
}: ErrorAlertProps) => {
  const { t } = useTranslation();

  const formattedErrorDetails: string[] = [];

  if (errorDetails) {
    if (errorDetails.name) {
      formattedErrorDetails.push(
        t('analytics.dashboard.error.detailName', { value: errorDetails.name })
      );
    }

    if (errorDetails.code) {
      formattedErrorDetails.push(
        t('analytics.dashboard.error.detailCode', { value: errorDetails.code })
      );
    }

    if (errorDetails.message) {
      formattedErrorDetails.push(
        t('analytics.dashboard.error.detailMessage', { value: errorDetails.message })
      );
    }

    if (errorDetails.timestamp) {
      const parsed = new Date(errorDetails.timestamp);
      const displayValue = Number.isNaN(parsed.getTime())
        ? errorDetails.timestamp
        : parsed.toLocaleString();
      formattedErrorDetails.push(
        t('analytics.dashboard.error.detailTimestamp', { value: displayValue })
      );
    }
  }

  const details =
    errorDetails &&
    (formattedErrorDetails.length > 0 ? (
      <ul className="space-y-0.5 text-xs text-muted-foreground">
        {formattedErrorDetails.map((detail) => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
    ) : (
      <p className="text-xs text-muted-foreground">
        {t('analytics.dashboard.error.detailsFallback')}
      </p>
    ));

  return (
    <Callout
      tone="error"
      title={t('analytics.dashboard.error.title')}
      className={className}
      action={
        onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            disabled={isRetrying}
            aria-keyshortcuts="r"
            title={t('analytics.dashboard.shortcuts.refresh')}
          >
            {isRetrying && <Spinner size="sm" />}
            {isRetrying
              ? t('analytics.dashboard.actions.refreshing')
              : t('analytics.dashboard.error.retry')}
          </Button>
        )
      }
    >
      <p>{error}</p>
      {details && (
        <div className="mt-2 space-y-1">
          <p className="eyebrow">{t('analytics.dashboard.error.detailsTitle')}</p>
          {details}
        </div>
      )}
    </Callout>
  );
};
